import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BracketButton from '../components/BracketButton';
import { company } from '../mock/mock';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const ContactPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    origin: '',
    destination: '',
    service: 'Flatbed',
    message: '',
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    // Save to localStorage as mock
    const existing = JSON.parse(localStorage.getItem('caprock_quotes') || '[]');
    existing.push({ ...form, ts: new Date().toISOString() });
    localStorage.setItem('caprock_quotes', JSON.stringify(existing));
    toast({
      title: 'Quote request received',
      description: 'A Caprock dispatcher will reach out within the hour.',
    });
    setForm({ name: '', email: '', phone: '', origin: '', destination: '', service: 'Flatbed', message: '' });
  };

  const inputCls =
    'w-full bg-transparent border-0 border-b border-black/25 focus:border-black outline-none py-3 font-body text-[15px] placeholder:text-black/40 transition-colors';

  return (
    <div className="bg-white">
      <Navbar />
      <div className="pt-32 pb-16 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-[1.5px] bg-black/60" />
          <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/60">Get In Touch</span>
        </div>
        <h1 className="big-display text-[64px] sm:text-[96px] lg:text-[140px] leading-[0.9]">
          Request A
          <br />
          <span className="text-black/30">Quote.</span>
        </h1>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50">Name</label>
                  <input required name="name" value={form.name} onChange={onChange} className={inputCls} placeholder="Your full name" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50">Company</label>
                  <input name="phone" value={form.phone} onChange={onChange} className={inputCls} placeholder="(optional)" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50">Email</label>
                  <input required type="email" name="email" value={form.email} onChange={onChange} className={inputCls} placeholder="you@company.com" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50">Service Needed</label>
                  <select name="service" value={form.service} onChange={onChange} className={`${inputCls} appearance-none`}>
                    <option>Flatbed</option>
                    <option>Oilfield / Hotshot</option>
                    <option>Logistics / Brokerage</option>
                    <option>Heavy Machinery</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50">Pickup Origin</label>
                  <input required name="origin" value={form.origin} onChange={onChange} className={inputCls} placeholder="City, State" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50">Destination</label>
                  <input required name="destination" value={form.destination} onChange={onChange} className={inputCls} placeholder="City, State" />
                </div>
              </div>
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50">Load Details</label>
                <textarea name="message" value={form.message} onChange={onChange} rows={4} className={inputCls} placeholder="Dimensions, weight, pickup date..." />
              </div>
              <div className="pt-4">
                <BracketButton onClick={onSubmit} variant="dark">Send Request</BracketButton>
              </div>
            </form>
          </div>

          <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-black/10">
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50 mb-6">
              Dispatch
            </div>
            <ul className="space-y-6 text-[15px] text-black/80">
              <li className="flex items-start gap-4">
                <MapPin className="w-5 h-5 mt-1" />
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-black/50 mb-1">Headquarters</div>
                  {company.address}
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Phone className="w-5 h-5 mt-1" />
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-black/50 mb-1">24/7 Dispatch</div>
                  <a href={`tel:${company.phone}`}>{company.phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Mail className="w-5 h-5 mt-1" />
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-black/50 mb-1">Email</div>
                  <a href={`mailto:${company.email}`}>{company.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Clock className="w-5 h-5 mt-1" />
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-black/50 mb-1">Hours</div>
                  {company.hours}
                </div>
              </li>
            </ul>

            <div className="mt-12 p-6 border border-black/15 bg-neutral-50">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50 mb-3">DOT / Compliance</div>
              <div className="font-body text-sm text-black/75 space-y-1">
                <div>USDOT #3841902</div>
                <div>MC #1207834</div>
                <div>Fully insured · $4M+ liability</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

export default ContactPage;
